import { Drill } from '../types/drill-types';
import { getDrillDomain, getTagValues } from '@/utils/drill-taxonomy';
import { drillSatisfiesFilterValue, postureValuesMatch } from './drill-filter-match';

/**
 * Same main dimensions as DrillLibrary filter row (Phase, Domain, Posture, Movement Limitation, Biomechanical Intent).
 * Ascending priority values → this display order after active-filter chips (priority 1).
 */
const MAIN_FILTER_DISPLAY_PRIORITIES: Record<string, number> = {
    Phase: 10,
    Domain: 20,
    Posture: 30,
    'Movement Limitation': 40,
    'Biomechanical Intent': 50,
};

interface DisplayTag {
    category: string;
    value: string;
    priority: number;
}

interface GetDisplayedTagsOptions {
    drill: Drill;
    activeFilters: { [key: string]: Set<string> };
    maxTagCount: number;
}

export const isValidTagValue = (value: unknown): value is string =>
    typeof value === 'string' && value.trim().length > 0;

export const getDisplayedTags = ({
    drill,
    activeFilters,
    maxTagCount,
}: GetDisplayedTagsOptions): Array<{ category: string; value: string }> => {
    if (!drill) {
        return [];
    }

    const tags: DisplayTag[] = [];
    const included = new Set<string>();

    const tryAddTag = (category: string, value: unknown, priority: number) => {
        if (!isValidTagValue(value)) return;
        const key = `${category}::${value}`;
        if (included.has(key)) return;
        included.add(key);
        tags.push({ category, value, priority });
    };

    // Prioritize active filters: show selected filter labels that this drill satisfies (same rules as DrillLibrary)
    Object.entries(activeFilters).forEach(([category, filterValues]) => {
        if (filterValues.size === 0) return;
        Array.from(filterValues).forEach(filterVal => {
            if (isValidTagValue(filterVal) && drillSatisfiesFilterValue(drill, category, filterVal)) {
                tryAddTag(category, filterVal, 1);
            }
        });
    });

    const priorityOnePostureFilters = tags
        .filter(t => t.category === 'Posture' && t.priority === 1)
        .map(t => t.value);

    const addMainCategoryTags = (category: string, basePriority: number) => {
        const values = getTagValues(drill, category);
        values.forEach(tagValue => {
            if (
                category === 'Posture' &&
                priorityOnePostureFilters.some(fv => postureValuesMatch(fv, tagValue))
            ) {
                return;
            }
            tryAddTag(category, tagValue, basePriority);
        });
    };

    addMainCategoryTags('Phase', MAIN_FILTER_DISPLAY_PRIORITIES.Phase);
    tryAddTag('Domain', getDrillDomain(drill), MAIN_FILTER_DISPLAY_PRIORITIES.Domain);
    addMainCategoryTags('Posture', MAIN_FILTER_DISPLAY_PRIORITIES.Posture);
    addMainCategoryTags('Movement Limitation', MAIN_FILTER_DISPLAY_PRIORITIES['Movement Limitation']);
    addMainCategoryTags('Biomechanical Intent', MAIN_FILTER_DISPLAY_PRIORITIES['Biomechanical Intent']);

    tryAddTag('Movement Pattern (Broad)', drill.tags?.['Movement Pattern (Broad)'], 60);

    const mainCategoryKeys = new Set(Object.keys(MAIN_FILTER_DISPLAY_PRIORITIES));

    // Remaining tags (categories not in the main filter row), lower priority
    Object.entries(drill.tags ?? {}).forEach(([category]) => {
        if (mainCategoryKeys.has(category)) return;
        const values = getTagValues(drill, category);
        values.forEach(tagValue => tryAddTag(category, tagValue, 100));
    });

    const sorted = tags.sort((a, b) => {
        if (a.priority === b.priority) {
            return a.value.localeCompare(b.value);
        }
        return a.priority - b.priority;
    });

    if (maxTagCount >= 0) {
        return sorted.slice(0, maxTagCount).map(({ category, value }) => ({ category, value }));
    }

    return sorted.map(({ category, value }) => ({ category, value }));
};


